import { readInlineData, graphql } from 'react-relay';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

import type { getShoppablePhotoData_tradePhoto$key } from './__generated__/getShoppablePhotoData_tradePhoto.graphql';
import type { getShoppablePhotoData_searchBrowsePhoto$key } from './__generated__/getShoppablePhotoData_searchBrowsePhoto.graphql';
import type { getShoppablePhotoData_tags$key } from './__generated__/getShoppablePhotoData_tags.graphql';
import type { getShoppablePhotoData_creators$key } from './__generated__/getShoppablePhotoData_creators.graphql';

// if shoppable, show right rail with shoppable items
export const getShoppablePhotoData = ({
    tradePhoto: tradePhotoRef,
    searchBrowsePhoto: searchBrowsePhotoRef,
}: {
    tradePhoto?: getShoppablePhotoData_tradePhoto$key | null;
    searchBrowsePhoto?: getShoppablePhotoData_searchBrowsePhoto$key | null;
}): {
    isShoppable: boolean;
    totalShoppableItems: number;
} => {
    const tradePhoto = readInlineData(
        graphql`
            fragment getShoppablePhotoData_tradePhoto on TradePhoto @inline {
                tags {
                    ...getShoppablePhotoData_tags
                }
                creators {
                    ...getShoppablePhotoData_creators
                }
            }
        `,
        tradePhotoRef || null
    );

    const searchBrowsePhoto = readInlineData(
        graphql`
            fragment getShoppablePhotoData_searchBrowsePhoto on PhotosSearchBrowsePhoto @inline {
                tags {
                    ...getShoppablePhotoData_tags
                }
                creators {
                    ...getShoppablePhotoData_creators
                }
            }
        `,
        searchBrowsePhotoRef || null
    );

    const photo = searchBrowsePhoto || tradePhoto;
    const tagsRef = (photo?.tags || []).filter(filterNulls);
    const creatorsRef = (photo?.creators || []).filter(filterNulls);

    const tags = tagsRef.map(tagRef =>
        readInlineData<getShoppablePhotoData_tags$key>(
            graphql`
                fragment getShoppablePhotoData_tags on TradePhotoTag @inline {
                    similarItems {
                        items {
                            serviceId
                        }
                    }
                }
            `,
            tagRef
        )
    );
    // !!! number of fetched items 'photoCreatorItems: items(first: 4)' should be same in 'PhotoRailContainer' file, otherwise we will double requests to BE
    const creators = creatorsRef.map(creatorRef =>
        readInlineData<getShoppablePhotoData_creators$key>(
            graphql`
                fragment getShoppablePhotoData_creators on PhotoCreator @inline {
                    photoCreatorItems: items(first: 4) {
                        serviceId
                    }
                }
            `,
            creatorRef
        )
    );

    const hasSimilarItems = tags.some(tag => (tag?.similarItems?.items || []).length > 0);
    const hasCreatorItems = creators.some(creator => (creator?.photoCreatorItems || []).length > 0);
    const isShoppable = hasCreatorItems || hasSimilarItems;

    return {
        isShoppable,
        totalShoppableItems: creators.length + tags.length,
    };
};
